import React, { useState, useEffect, useRef } from 'react';
import { 
    StyleSheet, 
    View, 
    ScrollView,
    KeyboardAvoidingView,
    TouchableWithoutFeedback,
    Dimensions,
    Keyboard,
    SafeAreaView,
    Animated,
    Platform
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';

import TouchableItem from '../../../components/TouchableItem';
import Colors from '../../../constants/Colors';
import DefaultInputMaskedMoney from '../../../components/DefaultInputMaskedMoney';
import DefaultText from '../../../components/DefaultText';
import NavContainer from '../../../components/NavContainer';
import DismissKeyboard from '../../../components/DismissKeyboard';

import * as potentialActions from '../../../store/actions/potential';

const HealthCostsScreen = props => {
    const healthCostData = useSelector(state => state.potential.healthCost);
    const healthCostAmountData = useSelector(state => state.potential.healthCostAmount);
    const [healthCost, setHealthCost] = useState(healthCostData);
    const [healthCostAmount, setHealthCostAmount] = useState(healthCostAmountData);
    const [healthCostAmountValid, setHealthCostAmountValid] = useState(healthCostAmountData ? true : false);

    const fadeAnim = useRef(new Animated.Value(0)).current;

    const fadeIn = () => {
        // Will change fadeAnim value to 1 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    const fadeOut = () => {
        // Will change fadeAnim value to 0 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    useEffect(() => {
        if (healthCost === true) {
            fadeIn();
        } else {
            fadeOut();
        }
    }, [healthCost])

    const dispatch = useDispatch();

    const inputChangeHandler = async (id, value, valid) => {
        setHealthCostAmount(value);
        setHealthCostAmountValid(valid);
        dispatch(potentialActions.setHealthCostAmount(value));
    };

    const sView = useRef();

    useEffect(() => {
        sView.current.scrollToEnd({ animated: true });
    })

    const selectionHandler = async (answer) => {
        setHealthCost(answer);
        dispatch(potentialActions.setHealthCost(answer));
        if (healthCostData === '') {
            if (answer === false) {
                props.navigation.navigate('Disability');
            } 
        }
    };

    const doneHandler = () => {
        if (healthCost === false || healthCostAmountValid) {
            props.navigation.navigate('Disability');
        }
    };


    return (
        <SafeAreaView style={styles.screen}>
        <ScrollView ref={sView}>
            <KeyboardAvoidingView behavior='position'>
                <DismissKeyboard>
                    <View style={styles.questionContainer}>
                <View style={styles.questionSubContainer}>
                <DefaultText style={styles.questionText}>Did you pay any out of pocket costs for your health ?</DefaultText>
                <DefaultText style={styles.note}>An estimate of your costs for the year, including doctor appointments, receipts for drugs.</DefaultText>
                <DefaultText style={styles.note}>Disability costs will be handled at the next question, don't add them in here.</DefaultText>
                </View>
                <View style={styles.answerContainer}>
                    <View style={styles.yesNoContainer}>
                        <TouchableItem
                            onSelectItem={() => selectionHandler(false)}
                            style={healthCost === false ? styles.activeAnswer : styles.passiveAnswer}
                        >
                            <DefaultText
                                style={healthCost === false ? styles.activeAnswerText : styles.passiveAnswerText}
                            >
                                No
                                </DefaultText>
                        </TouchableItem>
                        <TouchableItem
                            onSelectItem={() => selectionHandler(true)}
                            style={healthCost === true ? styles.activeAnswer : styles.passiveAnswer}
                        >
                            <DefaultText
                                style={healthCost === true ? styles.activeAnswerText : styles.passiveAnswerText}
                            >
                                Yes
                            </DefaultText>
                        </TouchableItem>
                        </View>
                    {healthCost === true && (
                        <Animated.View 
                        style={[
                            styles.inputContainer,
                            {
                              opacity: fadeAnim // Bind opacity to animated value
                            }
                          ]}>
                        <DefaultInputMaskedMoney
                            focus={!(healthCostAmountData > 0)}
                            id='health costs'
                            title="Health costs:"
                            errorMessage="Please enter a valid amount"
                            placeHolder="50'000"
                            keyboardType='numeric'
                            onInputChange={inputChangeHandler}
                            initialValue={healthCostAmount}
                            initiallyValid={healthCostAmountValid}
                            decimal
                            required
                        />
                        </Animated.View>
                    )}
                </View>

                <NavContainer
                                backShow={true}
                                nextShow={(healthCost === false || healthCostAmountValid === true)}
                                nextNav={doneHandler}
                                backNav={() => { props.navigation.goBack() }}
                            />

                </View>
                </DismissKeyboard>
            </KeyboardAvoidingView>
        </ScrollView>
        </SafeAreaView>
    );
};

export const healthCostsScreenOptions = navigationData => {
    return {
        title: "Health Costs",
        headerLeft: () =>
            null
    }
};

const styles = StyleSheet.create({
    screen: {
        flex: 1,
        backgroundColor: Colors.backColor,
        padding: 10,
        alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    },
    questionContainer: {        
        padding: 10,
        minHeight: Dimensions.get('window').height * 0.87,
        justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        maxWidth: 600,
        minWidth: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 600 : 0,
    },
    inputContainer: {
        flex: 1,
        paddingTop: 20
    },
    note: {
        paddingHorizontal: 10,
        marginBottom: 2,
        fontFamily: 'lato'
    },
    yesNoContainer: {
        justifyContent: 'space-between',
        flexDirection: 'row',
    },
    questionText: {
        fontFamily: 'lato-bold',
        color: Colors.darkBlue,
        fontSize: 20,
        paddingHorizontal: 10,
        marginBottom: 10,
    },
    questionSubContainer: {
        marginBottom: 40,
    },
    answerContainer: {
        justifyContent: 'space-between',
        // minHeight: 200,
        padding: 10,
    },
    passiveAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        backgroundColor: Colors.buttonGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 20,
        color: Colors.darkGreen,
    },
    passiveAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 16,
        color: Colors.darkGreen,
    },
});

export default HealthCostsScreen;